package fr.frux.feature.map

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import fr.frux.core.common.config.SiteLinks
import fr.frux.core.designsystem.components.ErrorState
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_attribution_collectivites
import fr.frux.feature.map.resources.map_attribution_osm
import fr.frux.feature.map.resources.map_loading_trees
import org.jetbrains.compose.resources.stringResource

/*
 * Etats plein ecran et attribution de MapScreen (#163) : ce que la carte doit toujours montrer
 * quelle que soit la disposition des controles flottants, extrait pour degager le fichier
 * principal.
 */

/**
 * Attribution des DONNEES d'arbres, due des qu'un arbre importe peut s'afficher (ADR 0010).
 * A ne pas confondre avec l'attribution du FOND de carte, que l'hote fournit deja sur web
 * (ADR 0007) : celle-ci porte sur le contenu et vaut donc sur toutes les plateformes.
 *
 * Quatre collectivites s'ajoutent a OpenStreetMap comme sources d'arbres (#501) : leurs noms ne
 * tiennent pas tous dans une mention de carte, la mention nominative vit sur la fiche arbre et aux
 * mentions legales. Deux segments fixes, chacun un lien independant :
 * "© les contributeurs OpenStreetMap" (omis quand [includeOsm] est faux, les tuiles creditant
 * deja OSM) puis "Donnees des collectivites" vers la section Sources des mentions legales.
 *
 * Plus de badge a part sur la carte : les deux segments rejoignent le panneau que le bouton (i) du
 * SDK deploie au chargement et replie au premier geste, a la suite du credit des tuiles
 * (`FruxMap.attributions`). Un seul panneau, montre et cache selon les memes regles, la ou trois
 * mentions se chevauchaient dans la rangee du bas.
 */
@Composable
internal fun dataAttributions(includeOsm: Boolean): List<String> =
    dataAttributions(
        osmLabel = stringResource(Res.string.map_attribution_osm),
        collectivitesLabel = stringResource(Res.string.map_attribution_collectivites),
        includeOsm = includeOsm,
    )

/**
 * Mentions HTML (un lien chacune), dans le format que le panneau d'attribution du SDK lit. Au
 * premier rendu web, `stringResource` rend "" : un segment vide ne pose aucun lien (garde deja en
 * vigueur pour OSM, cf. #493).
 */
internal fun dataAttributions(
    osmLabel: String,
    collectivitesLabel: String,
    includeOsm: Boolean,
): List<String> =
    buildList {
        if (includeOsm && osmLabel.isNotEmpty()) add(attributionLink(SiteLinks.OSM_COPYRIGHT, osmLabel))
        if (collectivitesLabel.isNotEmpty()) add(attributionLink(SiteLinks.LEGAL_NOTICE_SOURCES, collectivitesLabel))
    }

private fun attributionLink(
    url: String,
    label: String,
): String = "<a href=\"$url\">$label</a>"

/** Chargement en surcouche : la carte reste visible et montee derriere (#120). */
@Composable
internal fun LoadingOverlay(modifier: Modifier = Modifier) {
    Surface(
        modifier = modifier,
        shape = RoundedCornerShape(50),
        color = MaterialTheme.colorScheme.surface,
        shadowElevation = 2.dp,
    ) {
        Row(
            modifier =
                Modifier.padding(
                    horizontal = FruxSpacing.md,
                    vertical = FruxSpacing.sm,
                ),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            CircularProgressIndicator(modifier = Modifier.size(16.dp), strokeWidth = 2.dp)
            Spacer(modifier = Modifier.width(FruxSpacing.sm))
            Text(
                text = stringResource(Res.string.map_loading_trees),
                style = MaterialTheme.typography.bodyMedium,
            )
        }
    }
}

/**
 * Erreur en surcouche : voile translucide au-dessus de la carte, qui reste montee (#120).
 * Le voile est cliquable sans action pour absorber les gestes destines a la carte masquee.
 */
@Composable
internal fun ErrorOverlay(
    message: String,
    onRetry: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier =
            modifier
                .background(MaterialTheme.colorScheme.surface.copy(alpha = 0.9f))
                .clickable(enabled = true, onClick = {}),
        contentAlignment = Alignment.Center,
    ) {
        ErrorState(message = message, onRetry = onRetry)
    }
}
